import { useState } from 'react'

import { useFacilitySearchOptions } from '@/hooks/useFacilitySearchOptions'
import { usePermissions } from '@/hooks/usePermissions'
import { useToasts } from '@/hooks/useToasts'
import {
  MAX_FILENAME_LENGTH,
  MAX_PDF_SIZE,
  UPLOAD_ERROR_COMPONENTS,
  reportUploadError,
  useUpload,
} from '@/hooks/useUpload'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { BiListUl } from 'react-icons/bi'
import { HiOutlineCheckCircle } from 'react-icons/hi'

import {
  Box,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
} from '@chakra-ui/react'

import { Modal } from '@/components/overlays'
import {
  Button,
  FacilityOptionLabel,
  FormFileUploadControl,
  FormInputControl,
  FormInputSelectControl,
} from '@/components/ui'
import { useCreateStandardOperatingProcedureMutation } from '@/graphql/generated/hooks'
import { SopType, UploadedFileType } from '@/graphql/generated/schemas'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'
import {
  OptionI,
  createFacilityAddressFilter,
} from '@/utils/forms/optionBuilders'
import { getFacilityNoOptionsMessage } from '@/utils/forms/validation'

import { useFacilitySelectAll } from '../hooks'
import { SOP_ROLES } from '../utils/utils'
import { FacilitySelectHeader } from './FacilitySelectHeader'

interface CreateSOPModalIProps {
  isOpen: boolean
  onClose: () => void
}

interface FormInputsIProps {
  title: string
  facilities?: OptionI[]
  role: OptionI<SopType>
  file: FileList
}

const PDF_MIME_TYPE = 'application/pdf'

export const CreateSOPModal = ({ isOpen, onClose }: CreateSOPModalIProps) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const { shouldEnableCreateSOP } = usePermissions()
  const { showSuccess, showError } = useToasts()

  const [upload] = useUpload()
  const [createStandardOperatingProcedure] =
    useCreateStandardOperatingProcedureMutation()
  const {
    facilityOptions,
    isFacilitiesLoading,
    isFacilitiesError,
    setFacilityName,
  } = useFacilitySearchOptions()
  const {
    handleSubmit,
    register,
    control,
    errors,
    formState,
    trigger,
    watch,
    setValue,
  } = useForm<FormInputsIProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
  })
  const { isValid } = formState

  const { areAllFacilitiesSelected, handleSelectAllFacilities } =
    useFacilitySelectAll({
      watch,
      setValue,
      facilityOptions,
    })

  const onSubmit: SubmitHandler<FormInputsIProps> = async (values) => {
    trackEvent(`${BUTTON_PRESS} Upload SOP`, {
      sop_audience: values.role.value,
    })
    setIsLoading(true)

    const file = values.file.item(0)
    const sopTitle = values.title
    const facilityIds = values.facilities?.map((f) => f.value) || []

    try {
      const fileId = await upload({
        file,
        fileType: UploadedFileType.StandardOperatingProcedure,
        validationOptions: {
          maxFileSizeInBytes: MAX_PDF_SIZE,
          maxFileNameLength: MAX_FILENAME_LENGTH,
        },
      })

      try {
        const { data } = await createStandardOperatingProcedure({
          variables: {
            input: {
              name: sopTitle,
              externalId: fileId,
              facilityIds: facilityIds,
              type: values.role.value,
            },
          },
          refetchQueries: ['StandardOperatingProcedures'],
        })
        const sopReturnedTitle =
          data?.createStandardOperatingProcedure?.standardOperatingProcedure
            ?.name
        showSuccess(`Successfully added ${sopReturnedTitle}`)
        trackEvent('SOP Upload - Success', {
          sop_audience: values.role.value,
        })
        setIsLoading(false)
        onClose()
      } catch (error) {
        // Error creating SOP mutation
        trackEvent('SOP Upload - Failure', {
          sop_audience: values.role.value,
          error_type: 'mutation',
        })
        showError(error)
        setIsLoading(false)
      }
    } catch (error) {
      trackEvent('SOP Upload - Failure', {
        sop_audience: values.role.value,
        error_type: 'upload',
      })
      reportUploadError(error, UPLOAD_ERROR_COMPONENTS.CreateSOPModal)
      showError(error.message)
      setIsLoading(false)
    }
  }

  return (
    <Modal
      isOpen={isOpen}
      motionPreset='slideInBottom'
      onClose={onClose}
      size='xl'
    >
      <ModalOverlay />
      <ModalContent bg='#fff' rounded='20px'>
        <ModalHeader>
          <Box align-items='center' display='flex' m='20px auto'>
            <Box display='grid' placeItems='center'>
              <BiListUl size='32px' />
            </Box>
            <Box
              color='#353849'
              fontSize='26px'
              fontWeight='extrabold'
              letterSpacing='-0.4px'
              ml={2}
            >
              Add SOP
            </Box>
            <Spacer />
            <ModalCloseButton color='#000' opacity={0.4} position='initial' />
          </Box>
        </ModalHeader>
        <ModalBody p={0}>
          <Box px={6} py={2}>
            <Box mb={3}>
              <FormInputControl
                errorMessage={errors.title && errors.title.message}
                id='title'
                inputRef={register({
                  required: 'SOP title is required',
                })}
                isDisabled={isLoading}
                isInvalid={!!errors.title}
                label='SOP Title'
                placeholder='Enter a title for this SOP'
              />
            </Box>
            <Box mb={3}>
              <FacilitySelectHeader
                allFacilitiesSelected={areAllFacilitiesSelected}
                dataTestId='CreateSOPModal:selectAllFacilities'
                isDisabled={isFacilitiesLoading || facilityOptions.length === 0}
                onToggleSelectAll={handleSelectAllFacilities}
              />
              <FormInputSelectControl
                closeMenuOnSelect={false}
                control={control}
                data-testid='CreateSOPModal:facilities'
                defaultValue={null}
                filterOption={createFacilityAddressFilter}
                formatOptionLabel={(option, meta) => (
                  <FacilityOptionLabel
                    isValueContext={meta?.context === 'value'}
                    option={option}
                  />
                )}
                id='facilities'
                isClearable
                isInvalid={!!errors.facilities}
                isLoading={isFacilitiesLoading}
                isMulti
                isSearchable
                noOptionsMessage={({ inputValue }) =>
                  getFacilityNoOptionsMessage(
                    inputValue,
                    isFacilitiesError,
                    t('common:facility_search_error')
                  )
                }
                onInputChange={setFacilityName}
                options={facilityOptions}
                placeholder='Add facilities where this SOP applies'
              />
            </Box>
            <Box mb={3}>
              <FormInputSelectControl
                control={control}
                data-testid='CreateSOPModal:role'
                defaultValue={null}
                errorMessage={errors.role && 'Select a role'}
                id='role'
                isDisabled={isLoading}
                isInvalid={!!errors.role}
                label='Available To'
                options={SOP_ROLES}
                placeholder='Select the role that will have access to the SOP'
                rules={{ required: true }}
              />
            </Box>
            <Box mb={3}>
              <FormFileUploadControl
                accept='.pdf'
                errorMessage={errors.file && errors.file.message}
                id='file'
                inputRef={register({
                  required: 'SOP PDF is required',
                  validate: (fl: FileList) =>
                    fl?.[0]?.type === PDF_MIME_TYPE ||
                    'File should be in .pdf format',
                })}
                isDisabled={isLoading}
                isInvalid={!!errors.file}
                label='SOP PDF'
                onFileRemoved={() => trigger(['file'])}
                placeholder='Attach PDF'
              />
            </Box>
          </Box>
        </ModalBody>
        <ModalFooter pb={6} py={4}>
          <Button mr={3} onClick={onClose} variant='secondary'>
            Cancel
          </Button>
          <Button
            isDisabled={!shouldEnableCreateSOP || !isValid}
            isLoading={isLoading}
            leftIcon={<HiOutlineCheckCircle size='24px' />}
            loadingText='Uploading SOP'
            onClick={handleSubmit(onSubmit)}
          >
            Upload SOP
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
